/**
 * @fileoverview Time and Duration Formatting Utilities
 * 
 * Pure functions for formatting temporal values in the Squash Scoring Application.
 * These utilities handle the two distinct time display requirements:
 * - Real-time counters (seconds → mm:ss)
 * - Total durations (milliseconds → m:ss)
 * 
 * @module utils/formatters
 * @version 1.0.0
 * @since 2025-06-24
 * @author SquashScoringApp Team
 * 
 * Architecture Decision Record (ADR):
 * - WHY two formatters: Different input units (seconds vs ms) for different use cases
 * - WHY pure functions: Enables predictable testing, memoization, and zero side effects
 * - WHY padStart: Ensures consistent UI layout (always "00:00" format)
 * 
 * Performance Characteristics:
 * - O(1) time complexity for both functions
 * - Safe for high-frequency updates (timers, real-time displays)
 * - Memory efficient (no object allocation, string interpolation only)
 * 
 * Business Context:
 * - Squash matches can last 1-3 hours (need robust time display)
 * - Real-time game timers require second precision
 * - Match summaries show total duration in human-readable format
 */

// Fonctions de formatage extraites du code original

/**
 * Formats a time value in seconds to mm:ss display format.
 * 
 * Primary use case: Real-time counters and live timers where precision is seconds.
 * Used extensively in DuringMatch component for match/game duration display 
 * and Timer component for countdown/stopwatch functionality.
 * 
 * @description Converts numeric seconds to zero-padded mm:ss string format
 * @param {number} seconds - Time value in seconds (non-negative integer expected)
 * @returns {string} Formatted time string in "mm:ss" format (e.g., "02:30", "15:42")
 * 
 * @example
 * // Real-time game timer display
 * formatTime(0)     // → "00:00" (game start)
 * formatTime(90)    // → "01:30" (1 minute 30 seconds)
 * formatTime(3661)  // → "61:01" (1 hour 1 minute 1 second)
 * 
 * @example
 * // Integration with React Timer component
 * const TimerDisplay = ({ elapsedSeconds }: { elapsedSeconds: number }) => (
 *   <div className="timer">{formatTime(elapsedSeconds)}</div>
 * );
 * 
 * @throws {TypeError} Implicitly throws if seconds is not a number
 * @throws {RangeError} May produce unexpected results for negative values
 * 
 * @performance
 * - Execution time: ~0.1ms on modern browsers
 * - Memory allocation: Minimal (only result string)
 * - Safe for 60fps+ updates in timer components
 * 
 * @invariants
 * - Input seconds >= 0 for meaningful output
 * - Output always matches pattern /^\d{2,}:\d{2}$/
 * - Leading zero maintained for minutes < 10
 * 
 * @see {@link formatDuration} For millisecond-based duration formatting
 * @see DuringMatch component - Primary consumer for live timing
 * @see Timer component - Countdown/stopwatch display
 */
export const formatTime = (seconds: number): string => {
  // ✅ PROTECTION contre les valeurs négatives
  if (seconds < 0) {
    return "00:00";
  }
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats a duration in milliseconds to m:ss display format.
 * 
 * Primary use case: Summary displays and final match statistics where 
 * total elapsed time is stored as timestamps (milliseconds).
 * Used in AfterMatch component for displaying total match duration.
 * 
 * @description Converts millisecond duration to human-readable m:ss string format
 * @param {number} ms - Duration in milliseconds (non-negative expected)
 * @returns {string} Formatted duration string in "m:ss" format (e.g., "2:30", "125:42")
 * 
 * @example
 * // Match completion summary
 * formatDuration(0)       // → "0:00" (instantaneous)
 * formatDuration(90000)   // → "1:30" (1 minute 30 seconds)
 * formatDuration(3661000) // → "61:01" (1 hour 1 minute 1 second)
 * 
 * @example
 * // Integration with AfterMatch summary
 * const MatchSummary = ({ totalDuration }: { totalDuration: number }) => (
 *   <div className="summary">
 *     Total duration: {formatDuration(totalDuration)}
 *   </div>
 * );
 * 
 * @example
 * // Calculating from timestamps
 * const matchStart = Date.now();
 * // ... match plays ...
 * const matchEnd = Date.now();
 * const duration = formatDuration(matchEnd - matchStart);
 * 
 * @throws {TypeError} Implicitly throws if ms is not a number
 * @throws {RangeError} May produce unexpected results for negative values
 * 
 * @performance
 * - Execution time: ~0.15ms (slightly slower due to ms conversion)
 * - Memory allocation: Minimal (only result string)
 * - Typically called only on match completion (low frequency)
 * 
 * @invariants
 * - Input ms >= 0 for meaningful output
 * - Output always matches pattern /^\d+:\d{2}$/
 * - Seconds always zero-padded, minutes are not (natural reading)
 * 
 * @see {@link formatTime} For second-based real-time formatting
 * @see AfterMatch component - Primary consumer for match summaries
 * 
 * @architectural_note
 * Different from formatTime by:
 * 1. Input unit (milliseconds vs seconds)
 * 2. Minutes padding (natural vs zero-padded)
 * 3. Use case (summaries vs real-time)
 * This separation prevents unit confusion and optimizes for specific contexts.
 */
export const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Resolves a roundType code to its localized label.
 *
 * The roundType can come from two sources:
 * - the in-app setup `<select>` (lowercase keys: `pool`, `qf`, `sf`, `final`...)
 * - an external tournament system (MySquash), which sends uppercase `round_id`
 *   codes such as `DF` (demi-finale), `F` (final), `QF`, `Q`, `T1`..`T9`.
 *
 * Known codes are translated via `screens.setup.options.roundTypes.<code>`.
 * Unknown codes (e.g. `T1`..`T9`) fall back to the raw value, so the UI shows
 * `T1` rather than the bare translation key.
 *
 * @param t - translation function (must accept a `fallback` option)
 * @param roundType - raw roundType value, possibly undefined/empty
 * @returns localized label, raw value when no translation exists, or '' when empty
 */
export const formatRoundType = (
  t: (key: string, options?: { fallback?: string }) => string,
  roundType?: string
): string => {
  if (!roundType) return '';
  return t(`screens.setup.options.roundTypes.${roundType}`, { fallback: roundType });
};

/**
 * @namespace FormatterUtilities
 * @description
 * 
 * USAGE PATTERNS ACROSS PROJECT:
 * 
 * 1. **Real-time Updates** (formatTime)
 *    - DuringMatch: match/game timers
 *    - Timer: countdown/stopwatch displays
 *    - Frequency: 1Hz (every second)
 * 
 * 2. **Summary Displays** (formatDuration)
 *    - AfterMatch: total match duration
 *    - GameResult: individual game durations
 *    - Frequency: Once per completion
 * 
 * DESIGN DECISIONS:
 * 
 * 1. **Why Pure Functions?**
 *    - Predictable output for testing
 *    - Memoization-friendly for performance
 *    - No hidden state or side effects
 *    - Easy to reason about and debug
 * 
 * 2. **Why Two Formatters?**
 *    - Different input scales (seconds vs milliseconds)
 *    - Different display contexts (real-time vs summary)
 *    - Prevents unit conversion errors
 *    - Optimized for specific use cases
 * 
 * 3. **Why Zero-Padding Strategy?**
 *    - formatTime: Both mm:ss (consistent UI width)
 *    - formatDuration: Only ss (natural reading, variable width)
 *    - UI layout stability vs readability trade-off
 * 
 * MAINTENANCE GUIDELINES:
 * 
 * ✅ SAFE TO MODIFY:
 *    - Internal calculation logic (if output format unchanged)
 *    - Performance optimizations
 *    - Input validation additions
 * 
 * ⚠️  RISKY TO MODIFY:
 *    - Output format changes (breaks UI assumptions)
 *    - Function signatures (affects all consumers)
 *    - Padding behavior (affects layout consistency)
 * 
 * 🚫 DO NOT MODIFY:
 *    - Function names (used throughout codebase)
 *    - Pure function guarantee (enables optimization)
 * 
 * TESTING STRATEGY:
 * 
 * Critical test cases:
 * - Edge cases: 0, negative values, large numbers
 * - Boundary conditions: 59 seconds, 60 seconds, 3600 seconds
 * - Format consistency: padding, separator, length
 * - Performance: bulk operations, repeated calls
 * 
 * EXTENSION POINTS:
 * 
 * Future enhancements without breaking changes:
 * - Add locale-specific formatting (i18n)
 * - Add precision control (milliseconds display)
 * - Add abbreviation modes ("1h 30m" vs "90:00")
 * - Add validation with custom error types
 * 
 * PERFORMANCE CONSIDERATIONS:
 * 
 * - Both functions are O(1) complexity
 * - Safe for high-frequency calls (60fps+ timers)
 * - Consider memoization for repeated identical inputs
 * - String concatenation is optimized in modern JS engines
 * 
 * KNOWN LIMITATIONS:
 * 
 * - No input validation (assumes well-formed numeric input)
 * - No locale/internationalization support
 * - No timezone handling (works with relative durations)
 * - Maximum practical duration: ~24 days (JS number precision)
 */